/// <amd-module name="Wit/WorkItemForm/History/UpdatesList" />
import { useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { localeDateFormat } from "VSS/Platform/Date";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Intersection } from "VSSUI/Intersection";
import { IListItemProps, ListItem } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Splitter } from "VSSUI/Splitter";
import { Time } from "VSSUI/Time";
import { css } from "VSSUI/Util";
import * as DateUtil from "VSSUI/Utilities/Date";
import { ZeroData } from "VSSUI/ZeroData";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInUserTimeZone, getTimeStringFormat } from "Wit/Common/DateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as Resources from "Wit/WorkItemForm/Resources";
import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { IPartition, PartitionedList, StateCircle } from "../Common/Components";
import { IHistoryItem } from "../Common/Services";
import { getNewState, getUpdateDate, hasAttachmentUpdates, hasFieldChange, hasLinkUpdates } from "./HistoryUtils";
import { SingleLineComment } from "./SingleLineComment";
import { UpdateDetails } from "./UpdateDetails";
import { UpdateSummary } from "./UpdateSummary";
import { UpdatesFilterBar } from "./UpdatesFilterBar";

export interface IUpdatesListProps {
    updates: IHistoryItem[];
    pageContext: IVssPageContext;
}

const marginBelowUpdatesList = 20;
const calculatedListHeightTolerance = 2;

export function UpdatesList({ updates, pageContext }: IUpdatesListProps) {
    const [selectedUpdateId, setSelectedUpdateId] = useState<number | null>(null);
    const ref = useRef<HTMLDivElement | null>(null);
    const [updatesListHeight, setUpdatesListHeight] = useState<number>(0);
    const [splitterHeight, setSplitterHeight] = useState<number>(0);
    const [selectedUpdates, setSelectedUpdates] = useObservable<Array<IHistoryItem>>(updates);
    const [showFilter, setShowFilter] = useState<boolean>(false);

    useLayoutEffect(() => {
        const updateHeight = () => {
            const listRect = ref.current?.getBoundingClientRect();
            if (!listRect) {
                return;
            }
            const pageRect = document.querySelector(".work-item-form-page")?.getBoundingClientRect();
            if (!pageRect) {
                return;
            }

            // HACK: subtract a small margin from the list height so that this measurement works properly in the work item dialog
            const newHeight = pageRect.bottom - listRect.top - marginBelowUpdatesList;

            if (updatesListHeight && Math.abs(newHeight - updatesListHeight) < calculatedListHeightTolerance) {
                return;
            }

            setUpdatesListHeight(newHeight);

            const filterRect = document.querySelector(".updates-filter")?.getBoundingClientRect();
            if (filterRect) {
                const newSplitterHeight = pageRect.bottom - filterRect.top - marginBelowUpdatesList;
                setSplitterHeight(newSplitterHeight);
            }
        };

        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    });

    if (!updates.length) {
        return null;
    }

    return (
        <div className="flex-row">
            <Splitter
                className="full-width"
                onRenderNearElement={() => (
                    <div className="left-side-container">
                        <div className="toggle-button-updates-filter-container">
                            <Button
                                className="toggle-button-updates-filter subtle"
                                iconProps={{ iconName: "Filter" }}
                                onClick={() => {
                                    setShowFilter(!showFilter);
                                }}
                                ariaLabel={Resources.ToggleFilter}
                            />
                        </div>
                        <UpdatesFilterBar
                            pageContext={pageContext}
                            showFilter={showFilter}
                            updates={updates}
                            setSelectedUpdates={setSelectedUpdates}
                        />
                        <div className="updates-list-container v-scroll-auto" ref={ref} style={{ height: updatesListHeight }}>
                            {/* The Intersection component ties scrolling virtualization to the specified root element instead of the page */}
                            <Intersection rootElement={ref.current!}>
                                {/*
                                Intersection tries to get refs to all the children, which doesn't work for a function
                                component like PartitionedList. Throwing in an extra div fixes this more elegantly than
                                using forwardRef in PartitionedList. 
                                */}
                                <div>
                                    <Observer selectedUpdates={selectedUpdates}>
                                        {(props: { selectedUpdates: Array<IHistoryItem> }) => {
                                            return selectedUpdates.value.length > 0 ? (
                                                <PartitionedList
                                                    className="updates-list"
                                                    items={props.selectedUpdates}
                                                    getPartition={getPartition}
                                                    renderRow={(i, update, details) => (
                                                        <UpdateListItem
                                                            index={i}
                                                            details={details}
                                                            update={update}
                                                            isRowSelected={update.id === selectedUpdateId}
                                                            isSingleLinePreviewEnabled={
                                                                !isFeatureFlagEnabled(pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false)
                                                            }
                                                        />
                                                    )}
                                                    onSelect={(e, row) => setSelectedUpdateId(row.data.id)}
                                                />
                                            ) : (
                                                <ZeroData
                                                    className={"updates-zero-data-content"}
                                                    imagePath={getAssetLocation(
                                                        pageContext,
                                                        "ms.vss-work-web/new-work-item-form-content/Content/zerodata-no-results-found.svg"
                                                    )}
                                                    imageAltText={Resources.NoResultsFoundText}
                                                    secondaryText={Resources.NoResultsFoundText}
                                                />
                                            );
                                        }}
                                    </Observer>
                                </div>
                            </Intersection>
                        </div>
                    </div>
                )}
                onRenderFarElement={() => (
                    <div className="v-scroll-auto" style={{ height: showFilter ? splitterHeight : updatesListHeight }}>
                        <UpdateDetails update={updates.find(u => u.id === selectedUpdateId) ?? updates[0]} />
                    </div>
                )}
            />
        </div>
    );
}

interface IUpdateListItemProps extends IListItemProps<IHistoryItem> {
    update: IHistoryItem;
    isRowSelected: boolean;
    isSingleLinePreviewEnabled: boolean;
}

function UpdateListItem({ update, ...props }: IUpdateListItemProps) {
    const updateDate = getUpdateDate(update);

    return (
        <ListItem {...props}>
            <div className={css("update-list-item flex-row", props.isRowSelected && "selected-row-highlight")}>
                <Persona className="margin-8" size={PersonaSize.size32} identity={update.revisedBy} />
                <div className="update-list-item-details flex-grow flex-column justify-center">
                    <div className="flex-row justify-space-between">
                        <UpdateSummary className="update-list-item-ellipsis" update={update} />
                        <UpdateIcons update={update} />
                    </div>
                    <div
                        className={css(
                            "update-list-item-second-line",
                            "flex-row justify-space-between",
                            "font-size",
                            !props.isSingleLinePreviewEnabled && "right"
                        )}
                    >
                        {props.isSingleLinePreviewEnabled && <SingleLineComment className="update-list-item-ellipsis" update={update} />}
                        <Time
                            dateTime={updateDate}
                            ariaLabel={DateUtil.tooltipString(updateDate)}
                            getNextInterval={() => {
                                return -1;
                            }}
                            getTimeString={() => {
                                return localeDateFormat(updateDate, getTimeStringFormat(updateDate));
                            }}
                            tooltipProps={{
                                renderContent: () => DateUtil.tooltipString(updateDate)
                            }}
                        />
                    </div>
                </div>
            </div>
        </ListItem>
    );
}

interface IUpdateIconsProps {
    update: IHistoryItem;
}

function UpdateIcons({ update }: IUpdateIconsProps) {
    const showUserIcon = hasFieldChange(update, CoreFieldRefNames.AssignedTo);
    const newState = getNewState(update);
    const showCommentIcon = hasFieldChange(update, CoreFieldRefNames.History);
    const showTagIcon = hasFieldChange(update, CoreFieldRefNames.Tags);
    const showAttachmentIcon = hasAttachmentUpdates(update);
    const showLinkIcon = hasLinkUpdates(update);

    return (
        <span>
            {showUserIcon && <Icon className="icon-margin" iconName="Contact" tooltipProps={{ text: Resources.UpdateIconUserTooltip }} />}
            {newState && (
                <StateCircle className="icon-margin" state={newState} tooltipProps={{ text: format(Resources.UpdateIconStateTooltip, newState) }} />
            )}
            {showCommentIcon && <Icon className="icon-margin" iconName="Comment" tooltipProps={{ text: Resources.UpdateIconCommentTooltip }} />}
            {showAttachmentIcon && <Icon className="icon-margin" iconName="Attach" tooltipProps={{ text: Resources.UpdateIconAttachmentTooltip }} />}
            {showTagIcon && <Icon className="icon-margin" iconName="Tag" tooltipProps={{ text: Resources.UpdateIconTagTooltip }} />}
            {showLinkIcon && <Icon className="icon-margin" iconName="Link" tooltipProps={{ text: Resources.UpdateIconLinkTooltip }} />}
        </span>
    );
}

const partitionDaysAgo: readonly [string, number][] = [
    [Resources.Today, 0],
    [Resources.Yesterday, 1],
    [Resources.LastWeek, 7],
    [format(Resources.LastNDays, 30), 30]
];

const defaultPartition = Resources.Older;

function getPartition(update: IHistoryItem): IPartition {
    const updateDate = getUpdateDate(update);
    const now = getDateInUserTimeZone();

    for (const [partitionLabel, daysAgo] of partitionDaysAgo) {
        const boundary = new Date(now);
        boundary.setHours(0, 0, 0, 0);
        boundary.setDate(boundary.getDate() - daysAgo);

        if (updateDate.getTime() > boundary.getTime()) {
            return { label: partitionLabel, key: partitionLabel, order: daysAgo };
        }
    }

    return { label: defaultPartition, key: defaultPartition, order: Number.MAX_SAFE_INTEGER };
}
